import { ChangeDetectionStrategy, Component, DestroyRef, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { listStaggerAnimation } from '@examdojo/animation';
import { MarksEarnedBarComponent, PaceBoundaryBarComponent } from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { DateTimeService } from '@examdojo/core/date-time';
import { PracticeActivityService } from '@examdojo/practice-activity';
import { shareOneReplay } from '@examdojo/rxjs';
import { ImageComponent } from '@examdojo/ui/image';
import { LoaderComponent } from '@examdojo/ui/loader';
import { RiveDirective } from '@examdojo/ui/rive';
import { isNotNullish } from '@examdojo/util/nullish';
import { IonContent } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { gsap } from 'gsap';
import JSConfetti from 'js-confetti';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { FullscreenFlowStepComponent } from '../../../../shared/fullscreen-flow';
import { TopicPracticeV2Service } from '../../../practice/topic-practice/topic-practice-v2.service';

@Component({
  selector: 'dojo-activity-completion',
  imports: [
    LoaderComponent,
    ImageComponent,
    TranslocoPipe,
    RiveDirective,
    PaceBoundaryBarComponent,
    MarksEarnedBarComponent,
    ButtonComponent,
    IonContent,
  ],
  templateUrl: './activity-completion.component.html',
  styleUrl: './activity-completion.component.scss',
  host: { class: 'ion-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [listStaggerAnimation('.stats-item')],
})
export class ActivityCompletionComponent extends FullscreenFlowStepComponent {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly destroyRef: DestroyRef,
    private readonly topicPracticeService: TopicPracticeV2Service,
    private readonly practiceActivityService: PracticeActivityService,
  ) {
    super();
  }

  private readonly jsConfetti = new JSConfetti();

  // anmation
  private readonly animatedMarks = signal(0);
  private readonly animatedTimeMs = signal(0);

  private readonly paceBoundaryBar = viewChild<PaceBoundaryBarComponent>('paceBoundaryBar');

  readonly formattedMarks = signal('0');
  readonly formattedTime = signal('0s');
  readonly formattedPacePerMark = signal('0s');

  private readonly completionData$ = this.topicPracticeService.inProgressPracticeActivity$.pipe(
    map((activity) => activity?.id),
    filter(isNotNullish),
    distinctUntilChanged(),
    switchMap((activityId) => this.practiceActivityService.getPracticeActivityCompletionData(activityId)),
    tap((data) => {
      this.animateMarks(data.awarded_marks);
      this.animateTime(data.time_spent_ms);
      this.animatePacePerMark(data.pace_per_mark);
      setTimeout(() => this.triggerConfetti(), 500);
    }),
    shareOneReplay(),
  );

  readonly state = connectState({
    completionData: this.completionData$,
    screenDisplay: this.completionData$.pipe(
      map((data) => {
        const marksLevel = this.getMarksLevel(data.awarded_marks, data.total_marks);
        const paceLevel = this.getPaceLevel(data.pace_chart_index);
        const feedback = this.getFeedback(marksLevel, paceLevel);
        const sensei = this.getSenseiRiveFile(marksLevel, paceLevel);

        return {
          title: feedback.title,
          subtitle: feedback.subtitle,
          sensei: sensei,
        };
      }),
    ),
    isMultiQuestionActivity: this.topicPracticeService.isMultiQuestionActivity$,
  });

  handleNextStep(): void {
    this.goToNextStep().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private triggerConfetti() {
    this.jsConfetti.addConfetti({
      emojis: ['🎉', '⚡️', '💥', '✨', '🥳', '💪'],
      emojiSize: 70,
      confettiNumber: 50,
    });
  }

  private animateMarks(targetMarks: number): void {
    // Reset to 0 if we're reanimating
    this.animatedMarks.set(0);
    this.formattedMarks.set('0');

    const obj = { value: 0 };

    gsap.to(obj, {
      value: targetMarks,
      duration: 0.5,
      ease: 'power1.in',
      snap: { value: 1 },
      onUpdate: () => {
        const value = Math.ceil(obj.value);
        this.animatedMarks.set(value);
        this.formattedMarks.set(this.numberWithCommas(value));
      },
    });
  }

  private numberWithCommas(x: number): string {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  private getSenseiRiveFile(
    marksLevel: 'LOW' | 'MEDIUM' | 'HIGH',
    paceLevel: 'SLOW' | 'PERFECT' | 'FAST',
  ): { riveFile: string; artboard: string } {
    const fileMap = {
      LOW: {
        SLOW: { riveFile: 'assets/images/post-activity/grade/low_marks_slow_pace.riv', artboard: 'Artboard' },
        PERFECT: { riveFile: 'assets/images/post-activity/grade/low_marks_perfect_pace.riv', artboard: 'Artboard' },
        FAST: { riveFile: 'assets/images/post-activity/grade/low_marks_fast_pace.riv', artboard: 'Artboard' },
      },
      MEDIUM: {
        SLOW: { riveFile: 'assets/images/post-activity/grade/medium_marks_slow_pace.riv', artboard: 'Artboard' },
        PERFECT: { riveFile: 'assets/images/post-activity/grade/medium_marks_perfect_pace.riv', artboard: 'Artboard' },
        FAST: { riveFile: 'assets/images/post-activity/grade/medium_marks_fast_pace.riv', artboard: 'Artboard' },
      },
      HIGH: {
        SLOW: { riveFile: 'assets/images/post-activity/grade/high_marks_low_pace.riv', artboard: 'Artboard' },
        PERFECT: { riveFile: 'assets/images/post-activity/grade/high_marks_perfect_pace.riv', artboard: 'Artboard' },
        FAST: { riveFile: 'assets/images/post-activity/grade/high_marks_fast_pace.riv', artboard: 'Artboard' },
      },
    };

    return fileMap[marksLevel][paceLevel];
  }

  private getMarksLevel(awarded: number, total: number): 'LOW' | 'MEDIUM' | 'HIGH' {
    const percentage = (awarded / total) * 100;
    if (percentage <= 33) {
      return 'LOW';
    }
    if (percentage <= 66) {
      return 'MEDIUM';
    }
    return 'HIGH';
  }

  private getFeedback(
    marksLevel: 'LOW' | 'MEDIUM' | 'HIGH',
    paceLevel: 'SLOW' | 'PERFECT' | 'FAST',
  ): { title: string; subtitle: string } {
    const level = marksLevel.toLowerCase();
    const pace = paceLevel.toLowerCase();
    return {
      title: `grading.feedback.${level}.${pace}.title`,
      subtitle: `grading.feedback.${level}.${pace}.subtitle`,
    };
  }

  private getPaceLevel(pace: number): 'SLOW' | 'PERFECT' | 'FAST' {
    if (pace === 0) {
      return 'SLOW';
    }
    if (pace === 1) {
      return 'PERFECT';
    }
    return 'FAST';
  }

  private animateTime(targetTimeMs: number): void {
    // Reset to 0 if we're reanimating
    this.animatedTimeMs.set(0);
    this.formattedTime.set('0s');

    // Convert to seconds to reduce updates
    const targetTimeSec = targetTimeMs / 1000;
    const obj = { value: 0 };

    gsap.to(obj, {
      value: targetTimeSec,
      duration: 0.5,
      ease: 'power1.in',
      snap: { value: 1 }, // Snap to whole seconds
      onUpdate: () => {
        const valueInSeconds = Math.ceil(obj.value);
        this.animatedTimeMs.set(valueInSeconds * 1000); // Still store in ms for internal use
        this.formattedTime.set(
          this.dateTimeService.formatDurationShort(valueInSeconds, {
            unit: 'seconds',
            granularity: 2,
          }) || '0s',
        );
      },
    });
  }

  private animatePacePerMark(targetPacePerMark: number): void {
    this.formattedPacePerMark.set('0s');

    const obj = { value: 0 };

    gsap.to(obj, {
      value: targetPacePerMark,
      duration: 0.5,
      ease: 'power1.in',
      snap: { value: 1 },
      onUpdate: () => {
        const valueInSeconds = Math.ceil(obj.value);
        this.formattedPacePerMark.set(
          this.dateTimeService.formatDurationShort(valueInSeconds, {
            unit: 'seconds',
            granularity: 2,
          }) || '0s',
        );
      },
    });
  }
}
